import { NIKE_URL } from "@/constants/url";
import { Lang } from "@/data/translation";
import { createHttpErrorMessage } from "@/utils/create-http-error-message";
import { z } from "zod";

export type PortfolioDistributionCategory =
  | "holding-ratio"
  | "industrial-distribution";

export interface PortfolioHoldingsParams {
  investor: string;
  lang: Lang;
  category: PortfolioDistributionCategory;
}

const portfolioDistributionSchema = z.object({
  symbol: z.string(),
  quarter: z.string(),
  proportion: z.number(),
});

const industrialDistributionSchema = z.object({
  sectorCategory: z.string(),
  quarter: z.string(),
  proportion: z.number(),
});

const portfolioHoldingsSchema = z.object({
  data: z.union([
    portfolioDistributionSchema.array(),
    industrialDistributionSchema.array(),
  ]),
  message: z.string(),
});

export type PortfolioHoldingsData<T extends PortfolioDistributionCategory> =
  T extends "holding-ratio"
    ? z.infer<typeof portfolioDistributionSchema>
    : z.infer<typeof industrialDistributionSchema>;

export const getInvestorPortfolioHoldings = async <
  T extends PortfolioDistributionCategory
>({
  investor,
  lang,
  category,
}: PortfolioHoldingsParams): Promise<PortfolioHoldingsData<T>[]> => {
  const endpoint = `/13f/${investor}/portfolio-distribution/${category}`;
  const res = await fetch(`${NIKE_URL}${endpoint}`, {
    method: "GET",
    headers: { "Accept-Language": lang },
  });
  if (!res.ok) {
    const errorMessage = await createHttpErrorMessage(endpoint, res);
    throw new Error(errorMessage);
  }
  return portfolioHoldingsSchema.parse(await res.json())
    .data as PortfolioHoldingsData<T>[];
};
